/**
 * "Has this workspace connected anything yet?" — the one predicate behind
 * where a signed-in user lands (dashboard vs. the setup wizard).
 *
 * All five connectors count: a shared-number WhatsApp group, a BYO WhatsApp
 * session, a linked Signal account, a connected Slack account, or a
 * connected Granola account. Anything narrower strands that connector's
 * users in onboarding forever — none of them ever appears in the
 * shared-number group list.
 *
 * Importing history is deliberately NOT part of this: it's an optional step
 * users can do later from /sources, so it must not hold anyone in the wizard.
 */
import { useQuery } from '@tanstack/react-query';
import {
  type GranolaAccount,
  granolaAccountApi,
  type LinkedGroup,
  type SignalAccount,
  type SlackAccount,
  signalAccountApi,
  slackAccountApi,
  type WahaSession,
  wahaApi,
  wahaSessionApi,
} from '@/lib/api';
import { qk } from '@/lib/query-client';

export interface ConnectionSources {
  groups?: LinkedGroup[];
  wahaSessions?: WahaSession[];
  signalAccounts?: SignalAccount[];
  slackAccounts?: SlackAccount[];
  granolaAccounts?: GranolaAccount[];
}

/**
 * True once at least one connector is live. Half-finished links (pending QR,
 * mid-scan, or Granola's 'connecting' while the key is validated) don't
 * count — the user still has work to do in the wizard.
 */
export function hasConnection({
  groups = [],
  wahaSessions = [],
  signalAccounts = [],
  slackAccounts = [],
  granolaAccounts = [],
}: ConnectionSources): boolean {
  return (
    groups.some((g) => g.status !== 'removed') ||
    wahaSessions.some((s) => s.status === 'working') ||
    signalAccounts.some((a) => a.status === 'working') ||
    slackAccounts.some((a) => a.status === 'working') ||
    granolaAccounts.some((a) => a.status === 'active')
  );
}

export interface ConnectionStatus extends ConnectionSources {
  connected: boolean;
  isLoading: boolean;
}

/**
 * Connection state for routing decisions. A connector may be unconfigured
 * server-side (503) — `retry: false` keeps a missing one from stalling the
 * decision, and a failed query simply reads as "nothing connected there".
 */
export function useConnectionStatus(): ConnectionStatus {
  const groups = useQuery({
    queryKey: qk.wahaGroups,
    queryFn: () => wahaApi.groups(),
    retry: false,
  });
  const wahaSessions = useQuery({
    queryKey: qk.wahaSessions,
    queryFn: () => wahaSessionApi.list(),
    retry: false,
  });
  const signalAccounts = useQuery({
    queryKey: qk.signalAccounts,
    queryFn: () => signalAccountApi.list(),
    retry: false,
  });
  const slackAccounts = useQuery({
    queryKey: qk.slackAccounts,
    queryFn: () => slackAccountApi.list(),
    retry: false,
  });
  const granolaAccounts = useQuery({
    queryKey: qk.granolaAccounts,
    queryFn: () => granolaAccountApi.list(),
    retry: false,
  });

  const sources: ConnectionSources = {
    groups: groups.data,
    wahaSessions: wahaSessions.data,
    signalAccounts: signalAccounts.data,
    slackAccounts: slackAccounts.data,
    granolaAccounts: granolaAccounts.data,
  };

  return {
    ...sources,
    connected: hasConnection(sources),
    // Every query must settle before the answer is trustworthy — deciding on a
    // partial picture would bounce a connected user into the wizard.
    isLoading:
      groups.isLoading ||
      wahaSessions.isLoading ||
      signalAccounts.isLoading ||
      slackAccounts.isLoading ||
      granolaAccounts.isLoading,
  };
}
